import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import { getPlaymakerMetadata } from 'components/packages/Playmaker/utils/getMetadata';
import { content as ContentPropType } from 'lib/CustomPropTypes';

import FeaturedTease from '../FeaturedTease';
import FeaturedVideo from '../FeaturedVideo';
import Tease from '../Tease';
import TeaseLayout from '../TeaseLayouts';
import Title from '../Title';

const FEATURED_ITEM_INDEX = 0;

class CoverSpread extends React.Component {
  static propTypes = {
    content: ContentPropType.isRequired,
  };

  static childContextTypes = {
    setStickyVideoFlag: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      hasClickedToPlay: false,
      hasStickyVideo: false,
    };
  }

  shapeItem = (item = {}, packageMetadata = {}) => ({
    ...item,
    metadata: { // merge content and package metadata
      ...packageMetadata,
      ...(item || {}).metadata,
    },
  });

  getFeaturedItem = (items, packageMetadata) => {
    const itemAtIndex = get(items, `[${FEATURED_ITEM_INDEX}]`);
    return this.shapeItem(itemAtIndex, packageMetadata);
  };

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  };

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
  });

  onPlayClick = () => {
    this.setState({ hasClickedToPlay: true });
  };

  render() {
    const { content = {} } = this.props; // the package
    const {
      id: packageId,
      items = [],
      metadata: packageMetadata,
      context = {},
    } = content;

    const { isRailAdjacent } = context;

    const { hasStickyVideo, hasClickedToPlay } = this.state;
    if (!items || !items.length) return null;

    const {
      showBreakingHeader: breakingStatus,
      showEyebrow: showUnibrowStatus,
      title,
      titleUrl,
    } = packageMetadata || {};

    const {
      playmakerToggle,
      playmakerId,
    } = getPlaymakerMetadata(packageMetadata);
    const isPlaymaker = playmakerToggle === true;

    const featuredItem = this.getFeaturedItem(items, packageMetadata);
    const isBreaking = breakingStatus !== false;
    const showUnibrow = showUnibrowStatus !== false;

    // filtering in order to remove any articles that are returned as null
    // And remove first article since it is pulled into the featured item
    const teases = items.filter((item, index) => item && index > 0);

    // Split articles into two columns
    const firstTeases = teases.slice(0, Math.floor(teases.length / 2));
    const secondTeases = teases.slice(Math.floor(teases.length / 2), teases.length);

    const hasFeaturedItem = !!featuredItem.id; // package metadata is shaped onto item, so check id
    const isFeaturedVideo = featuredItem.type === 'video' || isPlaymaker;
    const isFeaturedTease = !isFeaturedVideo;

    return (
      <div
        className={classNames('pkg cover-spread', {
          'cover-spread--breaking': isBreaking,
          'cover-spread--stuck': hasStickyVideo,
          'cover-spread--rail': isRailAdjacent,
        })}
        data-test="cover-spread"
        data-packageid={packageId}
      >
        <div className="layout-grid-container">
          <div className="layout-grid-item grid-col-12">
            <div className="pkg">
              <div className="cover-spread__headline__wrapper">
                <Title title={title} titleUrl={titleUrl} isRailAdjacent={isRailAdjacent} />
              </div>

              <div className={classNames('default-cover-spread__wrapper', { 'cover-spread__rail': isRailAdjacent })}>
                <div className="default-cover-spread__teases default-cover-spread__column--1">
                  <TeaseLayout
                    layoutType="default"
                    columnType="__column--1"
                    totalTeases={items.length}
                    sectionTeaseCount={firstTeases.length}
                    isRailAdjacent={isRailAdjacent}
                    render={(layout) => (
                      firstTeases.map((tease, index) => (
                        <Tease
                          key={`cs-tease-${tease.id}`}
                          content={tease}
                          showInlineIcon
                          showUnibrow={showUnibrow}
                          layout={layout}
                          index={index}
                          isRailAdjacent={isRailAdjacent}
                        />
                      ))
                    )}
                  />
                </div>

                <button
                  className="default-cover-spread__feature default-cover-spread__column"
                  data-test="default-cover-spread__feature"
                  onClick={this.onPlayClick}
                  type="button"
                >
                  { hasFeaturedItem && isFeaturedVideo && (
                    // making this a "fully uncontrolled component with key"
                    // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
                    <FeaturedVideo
                      key={`${featuredItem.id}${isPlaymaker ? `-withPlaymakerId${playmakerId}` : ''}`}
                      content={featuredItem}
                      hasClickedToPlay={hasClickedToPlay}
                      isRailAdjacent={isRailAdjacent}
                    />
                  )}
                  { hasFeaturedItem && isFeaturedTease && (
                    <FeaturedTease
                      content={featuredItem}
                      showUnibrow={showUnibrow}
                      block="default-cover-spread-tease--featured"
                      headlineSizes="f7 f7-m"
                      isRailAdjacent={isRailAdjacent}
                    />
                  )}
                </button>

                <div
                  className="default-cover-spread__teases default-cover-spread__column--2"
                  data-test="default-cover-spread__teases"
                >
                  <TeaseLayout
                    layoutType="default"
                    totalTeases={items.length}
                    columnType="__column--2"
                    isRailAdjacent={isRailAdjacent}
                    render={(layout) => (
                      secondTeases.map((tease, index) => (
                        <Tease
                          key={`cs-tease-${tease.id}`}
                          content={tease}
                          showInlineIcon
                          showUnibrow={showUnibrow}
                          layout={layout}
                          index={firstTeases.length + index}
                          isRailAdjacent={isRailAdjacent}
                        />
                      ))
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default CoverSpread;
